import type { AxiosInstance, AxiosResponse } from 'axios'
import type { Ref } from 'vue'
import type { UseTrackingReturn } from '~/composables/useTracking'
import type { ConsentObjDto } from '~/types/AccountProfile'
import type { ContactConsents } from '~/types/WhatsAppReportFlow'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import { useFrontApi } from '~/composables/useFrontApi'
import { useTracking } from '~/composables/useTracking'
import { getAxiosResponseStatus } from '~/utils/axios'

export const useProfileVisibilityStore = defineStore('profileVisibility', () => {
  const { track }: UseTrackingReturn = useTracking()

  const consentObj: Ref<ConsentObjDto> = ref({
    allowPartnerContact: false,
    schoolPhoneContactConsent: null,
    personalitySharingConsent: null,
    documentsSharingConsent: null,
    companyContactConsent: null,
    whatsAppMarketingConsent: null,
    excludedProgramIds: null,
  })

  /**
   * Garde les choix enregistrés sur le compte, renvoyés par `GET /api/Profile/me`.
   * @param {ConsentObjDto} savedConsentObj - `consentObj` de la réponse.
   * @returns {void}
   */
  function setConsentObj(savedConsentObj: ConsentObjDto): void {
    consentObj.value = savedConsentObj
  }

  /**
   * Enregistre le consentement de contact par les écoles partenaires.
   * @param {boolean} allowed - `true` = visible dans le vivier.
   * @returns {Promise<void>}
   */
  async function savePartnerContactConsent(allowed: boolean): Promise<void> {
    const api: AxiosInstance = useFrontApi()
    await api.post('/api/Profile/allow-partner-contact', JSON.stringify(allowed), {
      headers: { 'Content-Type': 'application/json' },
    })
  }

  /**
   * Envoie un choix facultatif à son endpoint ; un échec est tracé sans bloquer l'élève.
   * @param {string} endpoint - Route FrontApi qui enregistre le choix.
   * @param {boolean | number[]} choice - Case cochée ou non, ou liste d'identifiants.
   * @returns {Promise<void>}
   */
  async function saveOptionalConsent(endpoint: string, choice: boolean | number[]): Promise<void> {
    try {
      await useFrontApi().post(endpoint, JSON.stringify(choice), {
        headers: { 'Content-Type': 'application/json' },
      })
    } catch (error: unknown) {
      track(TRACKING_EVENTS.CONSENT_SAVE_FAILED, {
        endpoint,
        status: getAxiosResponseStatus(error) ?? null,
      })
    }
  }

  /**
   * Enregistre le partage du profil de personnalité et des documents, le contact par des entreprises et les programmes retirés de la mise en relation.
   * @param {ContactConsents} consents - Choix de l'étape « Qui peut voir quoi ? ».
   * @param {number[]} excludedProgramIds - Programmes retirés de la liste par l'élève.
   * @returns {Promise<void>}
   */
  async function saveSharingConsents(
    consents: ContactConsents,
    excludedProgramIds: number[],
  ): Promise<void> {
    await Promise.all([
      saveOptionalConsent(
        '/api/Profile/personality-sharing-consent',
        consents.personalitySharingConsent ?? false,
      ),
      saveOptionalConsent(
        '/api/Profile/documents-sharing-consent',
        consents.documentsSharingConsent ?? false,
      ),
      saveOptionalConsent(
        '/api/Profile/company-contact-consent',
        consents.companyContactConsent ?? false,
      ),
      saveOptionalConsent('/api/Profile/excluded-programs', excludedProgramIds),
    ])
    consentObj.value = { ...consentObj.value, ...consents, excludedProgramIds }
  }

  /**
   * Enregistre l'accord pour recevoir les nouveautés et les rappels de PrePeers sur WhatsApp.
   * @param {boolean} hasAccepted - `true` si la case est cochée.
   * @returns {Promise<void>}
   */
  async function saveWhatsAppMarketingConsent(hasAccepted: boolean): Promise<void> {
    await saveOptionalConsent('/api/Profile/whatsapp-marketing-consent', hasAccepted)
    consentObj.value = { ...consentObj.value, whatsAppMarketingConsent: hasAccepted }
  }

  /**
   * Enregistre si les recommandations sont verrouillées pour les personnes à qui l'utilisateur partage son profil.
   * @param {boolean} recommendationsLocked - `false` = recommandations visibles sur le profil partagé.
   * @returns {Promise<boolean>} Verrouillage enregistré par le back.
   * @throws {Error} Si l'enregistrement échoue.
   */
  async function saveRecommendationsLocked(recommendationsLocked: boolean): Promise<boolean> {
    const api: AxiosInstance = useFrontApi()
    const response: AxiosResponse<boolean> = await api.post(
      '/api/private-profile/recommendations-locked',
      JSON.stringify(recommendationsLocked),
      { headers: { 'Content-Type': 'application/json' } },
    )
    track(TRACKING_EVENTS.PUBLIC_PROFILE_RECOMMENDATIONS_SHARING_SAVED, {
      recommendationsLocked: response.data,
    })
    return response.data
  }

  return {
    consentObj,
    setConsentObj,
    savePartnerContactConsent,
    saveSharingConsents,
    saveWhatsAppMarketingConsent,
    saveRecommendationsLocked,
  }
})
